import { combineEpics } from 'redux-observable';
import { concatMap, filter, from, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';

import * as optionGroupAPI from './option_groups.api';

import { catchAndLogError } from 'utils/common';

import module from './index';

import type { Epic } from 'redux-observable';
import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';
import type { LGRootState } from 'store/link_generator_store';

const { actions, selectors } = module;

/**
 * Dispatches an action to fetch an option group set for an array of template IDs
 */
export const fetchManyOptionGroupsEpic: Epic<Action, Action, RootState | LGRootState> = (action$) =>
  action$.pipe(
    filter(actions.fetchManyOptionGroups.match),
    concatMap(({ payload }) => payload.map((id) => actions.fetchOptionGroups.pending(id)))
  );

export const fetchOrderOptionsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.fetchOrderOptions.pending.match),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const ogs = selectors.allOrderOptions(state);

      if (ogs.length > 0) {
        return of(actions.fetchOrderOptions.fulfilled(ogs));
      }

      return from(optionGroupAPI.orderOptionGroups()).pipe(
        map((res) => actions.fetchOrderOptions.fulfilled(res)),
        catchAndLogError(actions.fetchOrderOptions.rejected())
      );
    })
  );
/**
 * Performs the fetch for option groups
 *
 * Responds with the values in state if already fetched
 */
export const fetchOptionGroupEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.fetchOptionGroups.pending.match),
    mergeMap(({ payload }) => {
      const productId = payload;
      const ogs = selectors.forTemplateId(state$.value, productId);

      if (ogs.length > 0) {
        return of(
          actions.fetchOptionGroups.fulfilled({ optionGroups: ogs, templateId: productId })
        );
      }

      return from(optionGroupAPI.allByTemplateId(productId)).pipe(
        map((res) =>
          actions.fetchOptionGroups.fulfilled({ optionGroups: res, templateId: productId })
        ),
        catchAndLogError(actions.fetchOptionGroups.rejected())
      );
    })
  );

const combined = combineEpics(
  fetchOptionGroupEpic,
  fetchManyOptionGroupsEpic,
  fetchOrderOptionsEpic
);
export default combined;
